import { FC, useEffect, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@new/common/ui/Button/Button';
import { Input } from '@new/common/ui/Input/Input';
import { MultiSelect } from '@new/common/ui/MultiSelect/MultiSelect';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import Information from '@new/common/ui/Info/Info';

import type { CourseCategory, TranscriptCourseInput } from '../types';

export interface EditableCourse extends TranscriptCourseInput {
  key: string;
}

const categoryIdsToValues = (categoryIds: number[] | null | undefined): string[] =>
  (categoryIds ?? []).map((id) => String(id));

const valuesToCategoryIds = (values: string[]): number[] => values.map((v) => Number(v));

interface CourseRowsEditorProps {
  courses: EditableCourse[];
  categories: CourseCategory[];
  onAdd: () => void;
  onUpdate: (key: string, patch: Partial<TranscriptCourseInput>) => void;
  onRemove: (key: string) => void;
  helpText: string;
  emptyText?: string;
}

export const CourseRowsEditor: FC<CourseRowsEditorProps> = ({
  courses,
  categories,
  onAdd,
  onUpdate,
  onRemove,
  helpText,
  emptyText = 'No courses added.',
}) => {
  const categoryOptions = categories.map((c) => ({ label: c.name, value: String(c.id) }));
  const endRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(courses.length);
  useEffect(() => {
    if (courses.length > prevCountRef.current) {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevCountRef.current = courses.length;
  }, [courses.length]);

  return (
    <fieldset className="m-0! flex flex-col gap-3 border-0 p-0">
      <SectionHeading
        title={`Courses${courses.length ? ` (${courses.length})` : ''}`}
        actions={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Plus className="size-4" />}
            onClick={onAdd}
            text="Add course"
          />
        }
      />

      <Information text={helpText} textClassName="[font-size:var(--fs-p4)]!" />

      {courses.length === 0 ? (
        <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {courses.map((course) => (
            <div key={course.key} className="flex items-center gap-2">
              <div className="w-80">
                <Input
                  placeholder="Course name"
                  value={course.rawName}
                  onChange={(v) => onUpdate(course.key, { rawName: v })}
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Code"
                  value={course.rawCode ?? ''}
                  onChange={(v) => onUpdate(course.key, { rawCode: v || null })}
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Grade"
                  value={course.grade ?? ''}
                  onChange={(v) => onUpdate(course.key, { grade: v || null })}
                />
              </div>
              <div className="flex-1">
                <Input<number | string>
                  type="number"
                  min={0}
                  step={1}
                  placeholder="Credit"
                  // Always display a whole number — round whatever the parent
                  // passes (AI/legacy values can be fractional, e.g. 1.5).
                  value={course.creditHours == null ? '' : Math.round(Number(course.creditHours))}
                  onChange={(v) =>
                    onUpdate(course.key, {
                      creditHours: v === '' ? null : Math.round(Number(v)),
                    })
                  }
                />
              </div>
              <div className="flex-1 min-w-60">
                <MultiSelect
                  value={categoryIdsToValues(course.categoryIds)}
                  onChange={(v) => onUpdate(course.key, { categoryIds: valuesToCategoryIds(v) })}
                  options={categoryOptions}
                  aria-label="Category"
                  placeholder="Category"
                  className="[&_[data-slot=select]]:h-[48px]! [&_[data-slot=select]]:rounded-[8px]! [&_[data-slot=select]]:border-[1.5px]! [&_[data-slot=select]]:border-solid! [&_[data-slot=select]]:border-[#E1E7EA]! [&_[data-slot=select]]:bg-[#FCFDFD]!"
                />
              </div>
              <button
                type="button"
                aria-label="Remove course"
                onClick={() => onRemove(course.key)}
                className="cursor-pointer p-2 text-(--neutral-black-NB600) hover:text-(--danger-D700)"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}
    </fieldset>
  );
};
